/**
 * FailureRecordPage (`/refinery/records/:recordId`): one failure record of
 * the team, built around its runs (the failure analysis views, the minimal
 * version Jacopo approved on 2026-10-29).
 *
 * The why sentence and the golden step the failing runs left come first,
 * then each run as a line of dots (RecordRuns), then everything else the
 * analyst wrote behind one disclosure, then what followed in task
 * generation. A model wrote the analysis, and the header says so. One read,
 * through /api/failure-analysis/records/{id}; the transcript a turn opens
 * is a second. Behind the refinery's nav gate, like the refinery.
 */

import { FileSearch } from 'lucide-react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { getApiErrorDetail, getApiErrorStatus } from '@/api/client'
import type { FailureRecordResponse } from '@/api/generated/model'
import { RecordRuns } from '@/components/refinery/RecordRuns'
import {
  FAILURE_TYPE_LABEL,
  RECORD_COPY,
  SPLIT_LABEL,
  analystSentence,
  difficultySentence,
  eligibilitySentence,
  fileName,
  followedSentences,
  refineryPath,
  runsSentence,
  stepSentence,
  tagLabel,
} from '@/components/refinery/record'
import { EvalBreadcrumb } from '@/components/evals/EvalBreadcrumb'
import { LoadingPanel } from '@/components/shared/LoadingScreen'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { EYEBROW } from '@/components/ui/eyebrow'
import { PageContainer } from '@/components/ui/page-container'
import { useFailureRecord } from '@/hooks/useFailureRecord'
import { useMinimumLoading } from '@/hooks/useMinimumSplash'

function Header({ record }: { record: FailureRecordResponse }): React.JSX.Element {
  return (
    <header className="space-y-2.5">
      <h1 className="font-serif text-2xl font-normal leading-tight">
        {record.label}
        {record.label_is_new && (
          <Badge variant="warning" className="ml-3 align-middle">
            {RECORD_COPY.newLabel}
          </Badge>
        )}
      </h1>
      <div className="flex flex-wrap items-center gap-2">
        {record.test_file !== '' && (
          <span className="font-mono text-xs" title={record.test_file}>
            {fileName(record.test_file)}
          </span>
        )}
        <Badge variant="outline">{FAILURE_TYPE_LABEL[record.failure_type]}</Badge>
        <Badge variant="outline">{tagLabel(record.category_sub)}</Badge>
        <Badge variant="outline">{SPLIT_LABEL[record.split]}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">{runsSentence(record)}</p>
      <p className="text-xs text-muted-foreground">{analystSentence(record)}</p>
    </header>
  )
}

function Why({ record }: { record: FailureRecordResponse }): React.JSX.Element {
  return (
    <section className="space-y-2">
      <blockquote className="border-l-2 border-foreground pl-4 font-serif text-lg leading-snug">
        {record.why}
      </blockquote>
      <p className="pl-[18px] text-sm text-muted-foreground">{stepSentence(record)}</p>
    </section>
  )
}

function Item({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="space-y-1">
      <h3 className={EYEBROW}>{label}</h3>
      {children}
    </div>
  )
}

function Lines({ items }: { items: string[] }): React.JSX.Element {
  if (items.length === 0) return <p className="text-muted-foreground">{RECORD_COPY.none}</p>
  return (
    <ul className="list-disc space-y-0.5 pl-4">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  )
}

function Details({ record }: { record: FailureRecordResponse }): React.JSX.Element {
  return (
    <details className="border-y border-border py-3">
      <summary className="flex cursor-pointer flex-wrap items-baseline gap-3">
        <span className="font-serif text-lg">{RECORD_COPY.details}</span>
        <span className="text-xs text-muted-foreground">{RECORD_COPY.detailsHint}</span>
      </summary>
      <div className="grid gap-x-8 gap-y-5 pt-4 text-sm md:grid-cols-2">
        <Item label={RECORD_COPY.goldenProcess}>
          <ol className="list-decimal space-y-0.5 pl-4">
            {record.golden_steps.map((step, index) => (
              <li key={`${index.toString()}-${step}`} className={index + 1 === record.step_index ? 'font-medium' : undefined}>
                {step}
              </li>
            ))}
          </ol>
        </Item>
        <Item label={RECORD_COPY.failureTurn}>
          <p>{record.turn}</p>
        </Item>
        <Item label={RECORD_COPY.stateAtFailure}>
          <p>{record.state_at_failure}</p>
        </Item>
        <Item label={RECORD_COPY.environmentFacts}>
          <Lines items={record.environment_facts} />
        </Item>
        <Item label={RECORD_COPY.confounds}>
          <Lines items={record.confounds} />
        </Item>
        <Item label={RECORD_COPY.difficulty}>
          <p>{difficultySentence(record)}</p>
        </Item>
        <Item label={RECORD_COPY.gradingRule}>
          <p>{record.grading_rule}</p>
        </Item>
        <Item label={RECORD_COPY.variationAxes}>
          <Lines items={record.variation_axes} />
        </Item>
        <Item label={RECORD_COPY.eligibility}>
          <p>{eligibilitySentence(record)}</p>
        </Item>
      </div>
    </details>
  )
}

function Followed({ record, refinery }: { record: FailureRecordResponse; refinery: string }): React.JSX.Element {
  return (
    <section aria-labelledby="record-followed" className="space-y-2">
      <h2 id="record-followed" className="font-serif text-lg">
        {RECORD_COPY.followedTitle}
      </h2>
      <p className="text-sm text-muted-foreground">{followedSentences(record.generation).join(' ')}</p>
      <Link to={refinery} className="text-sm underline underline-offset-2 hover:text-muted-foreground">
        {RECORD_COPY.seeTasks}
      </Link>
    </section>
  )
}

function RecordBody({ record, refinery }: { record: FailureRecordResponse; refinery: string }): React.JSX.Element {
  return (
    <>
      <Header record={record} />
      <Why record={record} />
      <section aria-labelledby="record-runs" className="space-y-3">
        <div className="space-y-1">
          <h2 id="record-runs" className="font-serif text-lg">
            {RECORD_COPY.runsTitle}
          </h2>
          <p className="text-sm text-muted-foreground">{RECORD_COPY.runsDescription}</p>
        </div>
        <RecordRuns record={record} />
      </section>
      <Details record={record} />
      <Followed record={record} refinery={refinery} />
    </>
  )
}

export default function FailureRecordPage(): React.JSX.Element {
  const { recordId = '' } = useParams<{ recordId: string }>()
  // The refinery's links carry the benchmark it showed; back links return to it.
  const [searchParams] = useSearchParams()
  const refinery = refineryPath(searchParams.get('benchmark'))
  const { data: record, isLoading, isError, error } = useFailureRecord(recordId, searchParams.get('round'))
  const loading = useMinimumLoading(isLoading)
  const notFound = isError && getApiErrorStatus(error) === 404

  return (
    <PageContainer className="space-y-7">
      <EvalBreadcrumb crumbs={[{ label: RECORD_COPY.crumb, to: refinery }, { label: recordId }]} />
      {loading && !isError && <LoadingPanel />}
      {notFound && (
        <EmptyState
          icon={FileSearch}
          title={RECORD_COPY.notFoundTitle}
          description={RECORD_COPY.notFoundDescription}
        />
      )}
      {isError && !notFound && (
        <p className="text-sm text-destructive">
          {RECORD_COPY.loadFailed} {getApiErrorDetail(error) ?? 'unknown error'}
        </p>
      )}
      {!loading && !isError && record !== undefined && <RecordBody record={record} refinery={refinery} />}
    </PageContainer>
  )
}
